/**
 * The selection model (spec 5.1): selected milestone points are a `Set<slotId|field>`
 * — ids + count only, never per-point chips. The chart's box-drag / click / shift-click
 * union is owned by SciChart's `DataPointSelectionModifier`, which reports the FULL
 * resulting set; the dialog stores it via {@link replaceWith}. These pure helpers cover
 * that store, the count, materialization back to points, and stacked-point detection.
 */

import { MILESTONE_ORDER } from './constants';
import { parseSelectionKey, selectionKey } from './utils';
import type { MergedDate, MilestoneKey, SlotId } from './types';

export type SelectionSet = ReadonlySet<string>;

/** An empty selection. */
export const EMPTY_SELECTION: SelectionSet = new Set<string>();

/**
 * Replaces the selection with a set of points — the store for the modifier's reported
 * final set (box-drag / click / shift-click union all resolve to a full set upstream).
 *
 * @param {Array} points - The new points.
 * @return {SelectionSet} The next selection.
 */
export function replaceWith(
  points: Array<{ slotId: SlotId; field: MilestoneKey }>
): SelectionSet {
  return new Set(points.map((p) => selectionKey(p.slotId, p.field)));
}

/**
 * The number of selected points.
 *
 * @param {SelectionSet} set - Current selection.
 * @return {number} The count.
 */
export function selectionCount(set: SelectionSet): number {
  return set.size;
}

/**
 * Materializes the selection back into `{ slotId, field }` points (for bulk shift /
 * revert-selection). Never surfaced as chips.
 *
 * @param {SelectionSet} set - Current selection.
 * @return {Array} The points.
 */
export function selectionToPoints(
  set: SelectionSet
): Array<{ slotId: SlotId; field: MilestoneKey }> {
  return Array.from(set).map(parseSelectionKey);
}

// ---------------------------------------------------------------------------
// Stacked-point detection
// ---------------------------------------------------------------------------

export interface StackedMilestone {
  field: MilestoneKey;
  date: MergedDate;
  /** Locked (reported) points render disabled in the picker. */
  locked: boolean;
}

/**
 * The milestones on one row that share a target ISO date — the stack under a click.
 * Includes locked members (shown disabled in the picker); the caller opens the picker
 * only when ≥2 members are editable and opens nothing for a fully locked stack.
 *
 * @param {object} dates - The row's effective dates.
 * @param {string} targetIso - The clicked ISO day.
 * @return {StackedMilestone[]} The stacked milestones, in lifecycle order.
 */
export function stackAt(
  dates: Record<MilestoneKey, MergedDate>,
  targetIso: string
): StackedMilestone[] {
  const stack: StackedMilestone[] = [];
  for (const field of MILESTONE_ORDER) {
    const date = dates[field];
    if (date?.value === targetIso) {
      stack.push({ field, date, locked: date.locked });
    }
  }
  return stack;
}

/**
 * Whether a stack should open the picker: ≥2 members and ≥2 of them editable.
 *
 * @param {StackedMilestone[]} stack - The stack from {@link stackAt}.
 * @return {boolean} True when the picker should open.
 */
export function stackOpensPicker(stack: StackedMilestone[]): boolean {
  const editable = stack.filter((s) => !s.locked);
  return stack.length >= 2 && editable.length >= 2;
}
